import { useCallback } from "react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { MatsuriError } from "matsuri-hooks"
import {
    requestRotateKeyboxNumber,
    useKeyboxNumber
} from "@/hooks/useKeyboxNumber"

export interface KeyboxRotation {
    keyboxNumber: string
    currentKeyboxNumber?: string
    isRotated: boolean
    isAvailable: boolean
    hasPhotoUrl: boolean
    rotate: (photoFile?: File) => Promise<void>
}

type KeyboxRotationWithLoadingTrue = Partial<KeyboxRotation> & {
    isLoading: true
    error: undefined
}

type KeyboxRotationWithLoadingFalse = KeyboxRotation & {
    isLoading: false
    error: undefined
}

interface KeyboxRotationWithError extends Partial<KeyboxRotation> {
    isLoading: false
    error: MatsuriError
}

type KeyboxRotationWithLoading =
    | KeyboxRotationWithLoadingTrue
    | KeyboxRotationWithLoadingFalse
    | KeyboxRotationWithError

export const useKeyboxRotation = (
    cleaningId: string
): KeyboxRotationWithLoading => {
    const { token } = useAuth()

    const keyboxNumber = useKeyboxNumber(cleaningId)
    const {
        keyboxNumber: keyboxNum,
        isLoading,
        error,
        refetch
    } = keyboxNumber as {
        keyboxNumber?: string
        isLoading: boolean
        error?: unknown
        refetch?: () => Promise<void>
    }

    const rotate = useCallback(
        async (photoFile?: File) => {
            if (error || isLoading) {
                return
            }
            if (!keyboxNum) {
                return
            }

            await requestRotateKeyboxNumber(
                token,
                cleaningId,
                keyboxNum,
                photoFile
            )
            await refetch?.()
        },
        [token, cleaningId, keyboxNum, isLoading, error, refetch]
    )

    if (keyboxNumber.isLoading) {
        return {
            isLoading: true,
            error: undefined
        }
    }

    if (keyboxNumber.error) {
        return {
            isLoading: false,
            error: keyboxNumber.error
        }
    }

    return {
        ...keyboxNumber,
        hasPhotoUrl: keyboxNumber.photoUrl ? true : false,
        rotate,
        isLoading: false,
        error: undefined
    }
}
